import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import GlobalSearchState from 'flarum/forum/states/GlobalSearchState';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import Pane from 'flarum/forum/utils/Pane';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import ForgotPasswordModal from 'flarum/forum/components/ForgotPasswordModal';
import SplitDropdown from 'flarum/common/components/SplitDropdown';
import Link from 'flarum/common/components/Link';
import Component from 'flarum/common/Component';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import TagLinkButton from 'flarum/tags/forum/components/TagLinkButton';
import TagSelectionModal from 'flarum/tags/common/components/TagSelectionModal';
import SubscriptionMenu from 'flarum/subscriptions/components/SubscriptionMenu';

import TaxonomyBreadcrumb from './components/TaxonomyBreadcrumb';
import startCitRegDiscussion from './discussionStart';
import { resumeCarReturn, installCarThreadLink } from './carThread';
import sidebarFallback from './sidebarFallback.generated';
import modelLinePresentation from './modelLinePresentation';
import installAnalytics from './analytics';
import installHomeReveal from './homeReveal';
import installPhoneUpload from './phoneUpload';
import installUploadPreviewNode from './uploadPreviewNode';

/*
 * Route *registration* (below, at the top of the initializer) has to be
 * synchronous and can't read app.forum -- confirmed by reading Flarum's own
 * Application.tsx boot(): initializers run in full BEFORE `this.forum` is
 * assigned from the store ("this.forum = this.store.getById('forums', '1')"
 * happens after the initializers loop, not before). Any initializer that
 * reads app.forum at its top level throws immediately ("X failed to
 * initialize") -- hit this for real on first deploy. So route shapes are a
 * small hardcoded list here, kept in sync by hand with TaxonomyGroups.php's
 * GROUPS constant -- the one place this extension can't be single-sourced
 * from PHP, because of when this code has to run, not by choice.
 *
 * Everything else below (app.route.tag override, the breadcrumb) runs later
 * -- at render/navigation time, well after boot() finishes -- so those DO
 * read app.forum live, same as fof/categories' own pattern.
 */
// Empty since 2026-10-29 (the "cars" group was unregistered; see REGISTERED_GROUPS).
const KNOWN_GROUPS = [];

function findTag(slug) {
  if (!slug) return null;
  return app.store.all('tags').find((tag) => tag.slug().toLowerCase() === String(slug).toLowerCase());
}

// A discussion can carry the catch-all General primary tag as well as its
// real section tag. Prefer the specific section everywhere; General remains
// the fallback for genuinely uncategorized discussions.
function preferredPrimaryTag(tags = []) {
  const primaries = tags.filter((tag) => tag.isPrimary && tag.isPrimary());

  return primaries.find((tag) => tag.slug() !== 'general') || primaries[0] || null;
}

function citregAuthBrand() {
  return (
    <span className="CitRegAuthBrand">
      <img src={app.forum.attribute('logoUrl')} alt="" className="CitRegAuthBrand-logo" />
      <span className="CitRegAuthBrand-copy">
        <span className="CitRegAuthBrand-name">CITROËN REGISTRY</span>
        <span className="CitRegAuthBrand-section">Forum</span>
      </span>
    </span>
  );
}

function citregAuthTabs(active, switchAction) {
  return (
    <div className="CitRegAuthTabs" role="tablist" aria-label="Account access">
      <button type="button" role="tab" aria-selected={active === 'login'} className={active === 'login' ? 'is-active' : ''} onclick={active === 'login' ? undefined : switchAction}>
        Log In
      </button>
      <button type="button" role="tab" aria-selected={active === 'signup'} className={active === 'signup' ? 'is-active' : ''} onclick={active === 'signup' ? undefined : switchAction}>
        Sign Up
      </button>
    </div>
  );
}

function brandAuthModal(ModalClass) {
  override(ModalClass.prototype, 'className', function (original) {
    return `${original()} CitRegAuthModal`;
  });
  override(ModalClass.prototype, 'title', () => citregAuthBrand());
}

function installAuthModalBranding() {
  brandAuthModal(LogInModal);
  brandAuthModal(SignUpModal);
  brandAuthModal(ForgotPasswordModal);

  override(LogInModal.prototype, 'content', function (original) {
    return [citregAuthTabs('login', this.signUp.bind(this)), ...original()];
  });
  override(SignUpModal.prototype, 'content', function (original) {
    return [citregAuthTabs('signup', this.logIn.bind(this)), ...original()];
  });
  override(ForgotPasswordModal.prototype, 'content', function (original) {
    return [<div className="CitRegAuthModal-taskTitle">Forgot Password</div>, ...[].concat(original())];
  });
}

function forumAssetUrl(path) {
  const baseUrl = (app.forum && app.forum.attribute('baseUrl')) || app.route('index');

  return `${String(baseUrl).replace(/\/$/, '')}/assets/${String(path).replace(/^\//, '')}`;
}

// Mirrors SeoBreadcrumbDriver::groupFromRouteName() on the PHP side exactly
// -- group is baked into the route name, not a route param, for the same
// reason (a real {group} wildcard would collide with the flat
// primary+secondary route; see extend.php).
function currentGroupSlug() {
  const routeName = app.current.get('routeName') || '';

  if (routeName.startsWith('citreg.group.')) return routeName.slice('citreg.group.'.length);

  const match = routeName.match(/^citreg\.tag\.(.+?)(\.sub)?$/);
  if (match && match[1] !== 'flat') return match[1];

  return null;
}

function activeSidebarScope(data) {
  let board = m.route.param('primary') || m.route.param('tags');
  const discussion = app.current.get('discussion');
  if (!board && discussion && discussion.tags) {
    const primary = preferredPrimaryTag(discussion.tags());
    board = primary && primary.slug();
  }
  return (data.boards[board] && data.boards[board].scope) || data.settings.defaultScope;
}

// The model marker belongs only to an exact primary model-board index.
// Group landing pages, secondary-tag views, discussion pages, and native
// catch-all tag routes are deliberately excluded even if they expose a
// primary-looking route parameter.
function railIdentityTag(data) {
  const routeName = app.current.get('routeName') || '';
  const isPrimaryBoardIndex = /^citreg\.tag\.[^.]+$/.test(routeName);

  if (!isPrimaryBoardIndex || m.route.param('secondary') || app.current.get('discussion')) return null;

  const tag = findTag(m.route.param('primary'));
  return tag && tag.isPrimary && tag.isPrimary() && presentationForTag(tag) && data.boards[tag.slug()] ? tag : null;
}

function activePrimaryTag() {
  const discussion = app.current.get('discussion');

  if (discussion && discussion.tags) {
    const primary = preferredPrimaryTag(discussion.tags());
    if (primary) return primary;
  }

  const routeSlug = m.route.param('primary') || m.route.param('tags');
  const routeTag = findTag(routeSlug);

  return routeTag && routeTag.isPrimary && routeTag.isPrimary() ? routeTag : null;
}

// The header brand (logo) goes to the site Home Page, as the phone brand does (operator, 2026-10-31). Core renders
// #home-link with the Forum's base URL and routes its clicks to the Forum index; the capture listener below runs
// first and stops that, so the browser follows the plain href.
window.addEventListener('click', (e) => {
  if (e.target instanceof Element && e.target.closest('#home-link')) e.stopPropagation();
}, true);

function renderHeaderContextLabel() {
  const headerTitle = document.querySelector('.Header-title');
  if (!headerTitle) return;

  const homeLink = headerTitle.querySelector('#home-link');
  if (homeLink && homeLink.getAttribute('href') !== '/') {
    homeLink.setAttribute('href', '/');
    homeLink.setAttribute('aria-label', 'Citroën Registry home');
  }
  // The "Citroën Registry" wordmark was the theme's ::before on .Header-title, outside the link; it now sits inside
  // #home-link as real text so the logo and the name are one Home Page link (the ::before is switched off in forum.less).
  if (homeLink && !homeLink.querySelector('.CitRegHeader-brandText')) {
    const brandText = document.createElement('span');
    brandText.className = 'CitRegHeader-brandText';
    brandText.textContent = 'Citroën Registry';
    homeLink.appendChild(brandText);
  }

  let contextLabel = headerTitle.querySelector('.CitRegHeader-contextLabel');
  if (!contextLabel) {
    contextLabel = document.createElement('span');
    contextLabel.className = 'CitRegHeader-contextLabel';
    headerTitle.appendChild(contextLabel);
  }

  const primary = activePrimaryTag();
  contextLabel.textContent = primary ? `${primary.name()} Forum` : 'Forum';
}

function presentationForTag(tag) {
  return tag ? modelLinePresentation[tag.slug()] || null : null;
}

function appendVnodeClass(attrs, className) {
  const key = Object.prototype.hasOwnProperty.call(attrs, 'class') ? 'class' : 'className';
  const classes = String(attrs[key] || '').split(/\s+/).filter(Boolean);
  if (!classes.includes(className)) classes.push(className);
  attrs[key] = classes.join(' ');
}

function vnodeHasClass(vnode, className) {
  if (!vnode || !vnode.attrs) return false;
  const classes = String(vnode.attrs.class || vnode.attrs.className || '').split(/\s+/);
  return classes.includes(className);
}

function findVnodeByClass(vnode, className) {
  if (Array.isArray(vnode)) {
    for (const child of vnode) {
      const match = findVnodeByClass(child, className);
      if (match) return match;
    }
    return null;
  }

  if (!vnode || typeof vnode !== 'object') return null;
  if (vnodeHasClass(vnode, className)) return vnode;
  return findVnodeByClass(vnode.children, className);
}

// Board-index excerpt preview, position cutoff (DM 2026-09-11, local-source
// only): tags each rendered DiscussionListItem vnode with its absolute
// position in the loaded list (0-based, in render order - matches core's
// own `pageNum * pageSize + itemNum` used for aria-posinset, without
// needing to separately track pageNum/itemNum ourselves, since walking the
// already-ordered render tree with one running counter is equivalent).
// "First page only" falls out of this for free: positions 0-2 are always
// the first page's first three items regardless of how many further pages
// get appended via "Load more" - no separate page-boundary check needed.
function tagDiscussionListItemPositions(vnode, counter) {
  if (Array.isArray(vnode)) {
    vnode.forEach((child) => tagDiscussionListItemPositions(child, counter));
    return;
  }

  if (!vnode || typeof vnode !== 'object') return;

  if (vnode.tag === DiscussionListItem) {
    vnode.attrs = vnode.attrs || {};
    vnode.attrs.citregListPosition = counter.value++;
    return;
  }

  if (vnode.children) tagDiscussionListItemPositions(vnode.children, counter);
}

function applyModelLineChoicePresentation(vnode, tag, navigationChoice = false) {
  const presentation = presentationForTag(tag);
  if (!presentation || !vnode || !vnode.attrs) return;

  const iconUrl = `url("${forumAssetUrl(presentation.navigationIcon)}")`;

  // Keep the marker on the choice root for semantic targeting and let the
  // custom property inherit as a fallback.
  appendVnodeClass(vnode.attrs, 'CitRegModelLineChoice');
  vnode.attrs.style = {
    ...(vnode.attrs.style || {}),
    '--citreg-model-line-nav-icon': iconUrl,
  };

  // Size the actual TagIcon vnode as well. Production proved that relying on
  // the ancestor class alone can leave Flarum's native 16px icon box in
  // control even while the inherited image variable survives. Directly
  // marking the painted node makes local and production rendering identical.
  const icon = findVnodeByClass(vnode.children, 'TagIcon');
  if (!icon) return;

  appendVnodeClass(icon.attrs, 'CitRegModelLineIcon');
  icon.attrs.style = {
    ...(icon.attrs.style || {}),
    '--citreg-model-line-nav-icon': iconUrl,
    width: '50px',
    height: '25px',
    margin: navigationChoice ? '-5px 7px -5px -28px' : '-5px 7px -5px -4px',
    borderRadius: '0',
    backgroundColor: 'transparent',
    backgroundImage: iconUrl,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    verticalAlign: 'middle',
  };
}

// The tag-selection modal has room to show a primary tag's full recognizable
// name (e.g. "Belphégor") rather than the short name it's stuck with
// elsewhere for tight UI (masthead, nav icon label) - user call, 2026-09-07.
// Flarum renders the tag's name as a plain string inside this choice's
// vnode.children tree; walk it and swap that exact string for familyName,
// leaving every other model line (no familyName set) untouched.
function replaceTagChoiceLabel(vnode, tag, presentation) {
  if (!presentation.familyName) return;
  const name = tag.name();
  const target = presentation.familyName;

  // Mithril stores an element vnode's sole plain-string child on `.text`
  // (confirmed by dumping the real tree: SelectTagListItem-name is
  // `{tag:'span', text:'N/P'}`, no `.children` at all) rather than wrapping
  // it in `.children` - `.children` only holds an array when there's more
  // than one child or a non-string one.
  const walk = (node) => {
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        if (node[i] === name) {
          node[i] = target;
        } else {
          walk(node[i]);
        }
      }
      return;
    }
    if (!node || typeof node !== 'object') return;
    if (node.text === name) {
      node.text = target;
    } else {
      walk(node.children);
    }
  };

  walk(vnode.children);
}

function markModelLineChoices(vnode) {
  if (Array.isArray(vnode)) {
    vnode.forEach(markModelLineChoices);
    return;
  }

  if (!vnode || typeof vnode !== 'object') return;

  const tagId = vnode.attrs && vnode.attrs['data-index'];
  if (tagId) {
    const tag = app.store.getById('tags', tagId);
    applyModelLineChoicePresentation(vnode, tag);
    const presentation = presentationForTag(tag);
    if (presentation) replaceTagChoiceLabel(vnode, tag, presentation);
  }

  if (vnode.children) markModelLineChoices(vnode.children);
}

function trackSidebarClick(tile, scope, position) {
  if (window._paq && window._paq.push) window._paq.push(['trackEvent', 'Forum Sidebar', 'Parts Diagram Click', tile.id, position]);
  if (typeof window.gtag === 'function') window.gtag('event', 'parts_diagram_click', { event_category: 'Forum Sidebar', event_label: tile.id, vehicle_scope: scope, position });
}

let sidebarManifestPromise;
let stickyTopicObserver;
let stickyTopicHero;
const composerPositionKey = 'citregComposerPosition';
const composerDragMinWidth = 768;
const preparedComposerHeaders = new WeakSet();

function storedComposerPosition() {
  try {
    return JSON.parse(window.sessionStorage.getItem(composerPositionKey) || 'null');
  } catch (error) {
    return null;
  }
}

function composerCanFloat(composer) {
  return window.innerWidth >= composerDragMinWidth && !composer.classList.contains('minimized') && !composer.classList.contains('fullScreen');
}

function dockComposer(composer) {
  const host = composer.closest('.App-composer');
  if (!host) return;
  if (host.classList.contains('CitRegComposer--floating')) host.classList.remove('CitRegComposer--floating');
  if (host.classList.contains('CitRegComposer--dragging')) host.classList.remove('CitRegComposer--dragging');
  if (host.style.getPropertyValue('--citreg-composer-left')) host.style.removeProperty('--citreg-composer-left');
  if (host.style.getPropertyValue('--citreg-composer-top')) host.style.removeProperty('--citreg-composer-top');
  if (host.style.getPropertyValue('--citreg-composer-width')) host.style.removeProperty('--citreg-composer-width');
}

function boundedComposerPosition(composer, left, top) {
  const rect = composer.getBoundingClientRect();
  const edge = 12;
  const headerAllowance = 54;
  const minimumTop = Math.max(edge, document.querySelector('.App-header')?.getBoundingClientRect().bottom || edge);
  const maximumLeft = Math.max(edge, window.innerWidth - rect.width - edge);
  const maximumTop = Math.max(minimumTop, window.innerHeight - Math.min(rect.height, headerAllowance) - edge);

  return {
    left: Math.min(Math.max(edge, left), maximumLeft),
    top: Math.min(Math.max(minimumTop, top), maximumTop),
  };
}

function floatComposerAt(composer, left, top, width) {
  if (!composerCanFloat(composer)) {
    dockComposer(composer);
    return;
  }

  const host = composer.closest('.App-composer');
  if (!host) return;
  if (!host.classList.contains('CitRegComposer--floating')) host.classList.add('CitRegComposer--floating');
  host.style.setProperty('--citreg-composer-width', `${Math.min(width || composer.getBoundingClientRect().width, window.innerWidth - 24)}px`);
  const position = boundedComposerPosition(composer, left, top);
  host.style.setProperty('--citreg-composer-left', `${position.left}px`);
  host.style.setProperty('--citreg-composer-top', `${position.top}px`);
}

function prepareDraggableComposer(composer) {
  const header = composer.querySelector('.ComposerBody-header');
  if (!header || preparedComposerHeaders.has(header)) return;
  preparedComposerHeaders.add(header);

  header.classList.add('CitRegComposer-dragHandle');

  const saved = storedComposerPosition();
  if (saved && Number.isFinite(saved.left) && Number.isFinite(saved.top)) {
    floatComposerAt(composer, saved.left, saved.top, saved.width);
  }

  header.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || !composerCanFloat(composer)) return;
    if (event.target.closest('a, button, input, textarea, select, [contenteditable="true"], .Composer-controls')) return;

    const initialRect = composer.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    let dragging = false;

    const move = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (!dragging && Math.hypot(deltaX, deltaY) < 4) return;
      if (!dragging) {
        dragging = true;
        composer.closest('.App-composer')?.classList.add('CitRegComposer--dragging');
        document.body.classList.add('CitRegComposer-isDragging');
        floatComposerAt(composer, initialRect.left, initialRect.top, initialRect.width);
      }

      moveEvent.preventDefault();
      floatComposerAt(composer, initialRect.left + deltaX, initialRect.top + deltaY, initialRect.width);
    };

    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      composer.closest('.App-composer')?.classList.remove('CitRegComposer--dragging');
      document.body.classList.remove('CitRegComposer-isDragging');

      if (!dragging) return;
      const rect = composer.getBoundingClientRect();
      window.sessionStorage.setItem(composerPositionKey, JSON.stringify({ left: rect.left, top: rect.top, width: rect.width }));
    };

    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', finish, { once: true });
    window.addEventListener('pointercancel', finish, { once: true });
  });
}

function installDraggableComposers() {
  document.querySelectorAll('.App-composer .Composer').forEach(prepareDraggableComposer);
}

// #content is Flarum's own grid item (column 1); .CitRegForumSidebar is the
// sidebar (column 2), aligned to the top of that same row (.App-content's
// own align-items:start). A site-wide notice (see
// repositionEmailConfirmationNotice below) needs to sit above #content
// without pushing the sidebar down with it - since a shared grid row's
// height/position is shared across every column in it, putting the notice in
// its own row (an earlier attempt) unavoidably moved the sidebar down too,
// confirmed live and explicitly unwanted (2026-09-05). Wrapping #content in
// a container that becomes the actual grid-column-1 item solves this
// cleanly: the notice and #content simply stack via normal block flow
// *inside* that wrapper, so growing/shrinking it never touches row-track
// sizing, and the sidebar - a wholly separate column-2 item - never moves.
// Idempotent; safe to call before #content exists yet (queued again by the
// same callers as repositionEmailConfirmationNotice below).
function ensureContentWrap() {
  const appContent = document.querySelector('.App-content');
  const content = appContent && appContent.querySelector(':scope > #content');
  if (!content) return null;

  let wrap = appContent.querySelector(':scope > .CitRegContentWrap');
  if (!wrap) {
    wrap = document.createElement('div');
    wrap.className = 'CitRegContentWrap';
    appContent.insertBefore(wrap, content);
    wrap.appendChild(content);
  }
  return wrap;
}

// Flarum core's alertEmailConfirmation() mounts the unconfirmed-email notice
// as a raw DOM node inserted directly before #content, with no CSS class of
// its own (only its rendered .Alert--emailConfirmation content has one).
// Moved into .CitRegContentWrap (see ensureContentWrap above), ahead of
// #content, and tagged with a real class of our own so forum.less can style
// it directly. Idempotent (guarded by the class check) - safe to call
// repeatedly from the existing MutationObserver below, since neither this
// node's mount timing nor #content's own readiness relative to boot is
// guaranteed.
function repositionEmailConfirmationNotice() {
  const alertEl = document.querySelector('.Alert--emailConfirmation');
  if (!alertEl) return;

  const notice = alertEl.parentElement;
  if (!notice || notice.classList.contains('CitRegNoticeSlot')) return;

  const wrap = ensureContentWrap();
  if (!wrap) return;

  notice.classList.add('CitRegNoticeSlot');
  wrap.prepend(notice);
}

function syncStickyTopicVisibility() {
  const hero = document.querySelector('.DiscussionPage .DiscussionHero');
  const nav = document.querySelector('.DiscussionPage-nav');

  if (!nav) return;

  if (!hero) {
    nav.classList.add('is-hero-past');
    return;
  }

  if (hero === stickyTopicHero) return;

  if (stickyTopicObserver) stickyTopicObserver.disconnect();

  stickyTopicHero = hero;
  stickyTopicObserver = new IntersectionObserver(
    ([entry]) => {
      nav.classList.toggle('is-hero-past', !entry.isIntersecting || entry.boundingClientRect.bottom <= 63);
    },
    { rootMargin: '-63px 0px 0px 0px', threshold: 0 }
  );
  stickyTopicObserver.observe(hero);
}

// Literature images and links come from the standing local site server.
// The JSON manifest itself is deliberately fetched same-origin below; the
// local Forum installs a narrow bridge matching production's URL contract.
const LOCAL_LITERATURE_ORIGIN = 'http://127.0.0.1:8000';

function sidebarManifestData() {
  if (!sidebarManifestPromise) {
    sidebarManifestPromise = fetch('/shared/include/forum_sidebar/manifest.php', { cache: 'no-store' })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error(`Sidebar manifest ${response.status}`)))
      // The endpoint is the runtime authority. A bundled snapshot can drift
      // from its board map and silently display the wrong family; on failure,
      // leave this optional rail empty and preserve the page.
      .catch(() => sidebarFallback);
  }
  return sidebarManifestPromise;
}

// SP-55 (Forum UX Streamline), operator 2026-10-31: "in DT view, the rt. col top card should be the rotating stats
// reports - as on the HP". The site serves the Home Page inset as a fragment without its script
// (/shared/include/forum_sidebar/stats_inset.php); it sits above the rail's other cards and turns here exactly as on the
// Home Page: every 7 seconds, paused under the pointer or focus, never by itself under reduced motion; the dots pick a
// report. If the statistics are unavailable the rail renders as before.
let sidebarStatsPromise;
let sidebarStatsTimer;

function sidebarStatsHtml() {
  if (!sidebarStatsPromise) {
    sidebarStatsPromise = fetch('/shared/include/forum_sidebar/stats_inset.php', { cache: 'no-store' })
      .then((response) => (response.status === 200 ? response.text() : ''))
      .catch(() => '');
  }
  return sidebarStatsPromise;
}

function sidebarStatsCard(html) {
  const wrapper = document.createElement('div');
  wrapper.className = 'CitRegForumSidebar-component CitRegForumSidebar-component--stats';
  wrapper.innerHTML = html;
  const card = wrapper.querySelector('[data-hpi]');
  if (!card) return null;
  const items = card.querySelectorAll('.hpi-item');
  const dots = card.querySelectorAll('.hpi-dots button');
  let at = 0;
  let held = false;
  const show = (i) => {
    items[at].classList.remove('is-on'); items[at].setAttribute('aria-hidden', 'true'); items[at].tabIndex = -1; dots[at].removeAttribute('aria-current');
    at = i;
    items[at].classList.add('is-on'); items[at].removeAttribute('aria-hidden'); items[at].removeAttribute('tabindex'); dots[at].setAttribute('aria-current', 'true');
  };
  dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
  card.addEventListener('mouseenter', () => { held = true; });
  card.addEventListener('mouseleave', () => { held = false; });
  card.addEventListener('focusin', () => { held = true; });
  card.addEventListener('focusout', () => { held = false; });
  clearInterval(sidebarStatsTimer);
  if (items.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    sidebarStatsTimer = setInterval(() => {
      if (!card.isConnected) { clearInterval(sidebarStatsTimer); return; }
      if (!held && !document.hidden) show((at + 1) % items.length);
    }, 7000);
  }
  return wrapper;
}

// Bumped on every call and captured per-call below so a call that started
// earlier but resolves later (a real race: this function is async and gets
// re-triggered on every mutation the MutationObserver below sees during
// Flarum's initial render, which can fire many times in quick succession)
// can detect it's stale and bail instead of clobbering a newer, correct
// render with outdated data.
let sidebarRenderGeneration = 0;

async function renderSidebar(sidebar) {
  const generation = ++sidebarRenderGeneration;
  const [data, statsHtml] = await Promise.all([sidebarManifestData(), sidebarStatsHtml()]);
  if (generation !== sidebarRenderGeneration) return;

  if (!data || !data.settings || !Array.isArray(data.settings.components) || !data.boards || !data.manifests) {
    sidebar.dataset.signature = 'unavailable';
    sidebar.replaceChildren();
    return;
  }

  const scope = activeSidebarScope(data);
  const tiles = data.manifests[scope] || data.manifests[data.settings.defaultScope] || [];
  const identityTag = railIdentityTag(data);
  const identitySlug = identityTag ? identityTag.slug() : '';
  const signature = `${app.current.get('routeName') || ''}:${identitySlug}:${scope}:${tiles.map((tile) => tile.id).join(',')}`;
  if (sidebar.dataset.signature === signature) return;
  sidebar.dataset.signature = signature;
  sidebar.replaceChildren();
  const assetOrigin = window.location.hostname === 'localhost' ? LOCAL_LITERATURE_ORIGIN : '';
  const statsCard = statsHtml ? sidebarStatsCard(statsHtml) : null;
  // SP-55 (Forum UX Streamline), operator 2026-10-31: "Make the Forum rt column sticky". The column's cells sit in one
  // inner box that sticks below the header; the aside keeps its full-height border.
  const inner = document.createElement('div');
  inner.className = 'CitRegForumSidebar-inner';
  sidebar.appendChild(inner);
  if (statsCard) inner.appendChild(statsCard);
  data.settings.components.filter((item) => item.enabled).sort((a, b) => a.order - b.order).forEach((component) => {
    const wrapper = document.createElement('div');
    wrapper.className = `CitRegForumSidebar-component CitRegForumSidebar-component--${component.type}`;
    if (component.sticky) wrapper.classList.add('is-sticky');
    if (component.type === 'model-identity' && identityTag) {
      const presentation = presentationForTag(identityTag);
      const board = data.boards[identityTag.slug()];
      // Falls back to the main Literature gateway when a registered board has
      // no per-model gateway anchor yet (e.g. a Forum board onboarded before
      // its Literature family-card section exists) - the contract validator
      // requires literatureGateway on every board today, but this keeps the
      // renderer itself correct independent of that always holding true.
      const gatewayHref = (board && board.literatureGateway) || '/lit/';
      const link = document.createElement('a');
      const image = document.createElement('img');
      const label = document.createElement('span');
      link.className = 'CitRegForumSidebar-identity';
      link.href = `${assetOrigin}${gatewayHref}`;
      link.setAttribute('aria-label', `${identityTag.name()} Literature gateway`);
      image.src = forumAssetUrl(presentation.railIdentityImage);
      image.alt = '';
      image.decoding = 'async';
      image.loading = 'eager';
      image.addEventListener('error', () => wrapper.remove());
      label.className = 'CitRegForumSidebar-identity-label';
      label.textContent = 'Literature';
      link.append(image, label);
      const divider = document.createElement('div');
      divider.className = 'CitRegForumSidebar-identity-divider';
      wrapper.append(link, divider);
    }
    if (component.type === 'parts-diagram-tiles') tiles.forEach((tile, index) => {
      const link = document.createElement('a');
      const figure = document.createElement('figure');
      const image = document.createElement('img');
      const caption = document.createElement('figcaption');
      link.className = 'CitRegForumSidebar-tile';
      link.href = `${assetOrigin}${tile.href}`;
      link.setAttribute('aria-label', tile.alt);
      link.addEventListener('click', () => trackSidebarClick(tile, scope, index + 1));
      image.src = `${assetOrigin}${tile.image}`;
      image.alt = tile.alt;
      image.width = tile.width;
      image.height = tile.height;
      image.decoding = 'async';
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.addEventListener('error', () => link.remove());
      caption.textContent = `${tile.caption} · p.${tile.page}`;
      figure.append(image, caption);
      link.appendChild(figure);
      wrapper.appendChild(link);
    });
    if (wrapper.childElementCount) inner.appendChild(wrapper);
  });
  watchSidebarSticky(inner);
}

// The sticky inner box: held at the header's foot when it fits the window. A taller one scrolls with the page until its
// last cell is in view and is held there (its top goes negative by what does not fit), so no cell is ever out of reach.
let sidebarStickyObserver = null;

function syncSidebarSticky(inner) {
  if (!inner.isConnected) return;
  const styles = getComputedStyle(inner);
  const header = parseFloat(styles.getPropertyValue('--header-height')) || 0;
  const gap = parseFloat(styles.getPropertyValue('--fr-rail-sticky-top')) || 0;
  inner.style.top = `${Math.min(header + gap, window.innerHeight - inner.offsetHeight - gap)}px`;
}

function watchSidebarSticky(inner) {
  if (sidebarStickyObserver) sidebarStickyObserver.disconnect();
  if (!sidebarStickyObserver) window.addEventListener('resize', () => {
    const current = document.querySelector('.CitRegForumSidebar-inner');
    if (current) syncSidebarSticky(current);
  });
  sidebarStickyObserver = new ResizeObserver(() => syncSidebarSticky(inner));
  sidebarStickyObserver.observe(inner);
  syncSidebarSticky(inner);
}

// SP-55 (Forum UX Streamline): the Live Localhost Tuner (Tools/Source/live_tuner/, served at /_tuner/ by the local
// Apache) on a loopback host with ?tune=1 only, so production never loads it. Started once the routed page has
// rendered, so the tuner finds the elements its values act on.
// SP-55 (Forum UX Streamline): a discussion's board identity, shared by the desktop header, the tablet header and
// the phone nav row. A model line shows its own drawing; a board without one (Sandbox, General) the chevron.
function discussionIdentity(discussion) {
  const tags = discussion.tags() || [];
  const primary = preferredPrimaryTag(tags);
  if (!primary) return null;
  const presentation = presentationForTag(primary);
  const image = presentation
    ? { src: forumAssetUrl(presentation.discussionImage), width: presentation.discussionImageWidth, height: presentation.discussionImageHeight }
    : { src: app.forum.attribute('logoUrl') };
  return {
    primary,
    secondaryTags: tags.filter((tag) => !(tag.isPrimary && tag.isPrimary())),
    imageLink: (className) => (
      <Link href={app.route.tag(primary)} className={`${className} ${className}--${primary.slug()}`} title={`${primary.name()} Forum`} aria-label={`${primary.name()} Forum`}>
        <img src={image.src} alt="" width={image.width} height={image.height} />
      </Link>
    ),
  };
}

// Phone nav row topic (operator, 2026-10-31: "Post Topic - if excessive size, then its a popout, hideaway bubble").
// The title truncates to the room left; a tap opens a bubble with the board, its tags and the full title, and a tap
// anywhere else closes it. The caret shows only when the title is cut short.
class PhoneTopic extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.open = false;
    this.truncated = false;
    this.closeOutside = (e) => {
      if (this.open && this.element && !this.element.contains(e.target)) {
        this.open = false;
        m.redraw();
      }
    };
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    document.addEventListener('click', this.closeOutside, true);
    this.measure();
    this.resizeObserver = window.ResizeObserver ? new ResizeObserver(() => this.measure()) : null;
    if (this.resizeObserver) this.resizeObserver.observe(this.element);
  }

  onupdate(vnode) {
    super.onupdate(vnode);
    this.measure();
  }

  onremove(vnode) {
    super.onremove(vnode);
    document.removeEventListener('click', this.closeOutside, true);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  measure() {
    const title = this.element && this.element.querySelector('.CitRegPhoneTopic-title');
    const truncated = !!title && title.scrollWidth > title.clientWidth + 1;
    if (truncated !== this.truncated) {
      this.truncated = truncated;
      m.redraw();
    }
  }

  view() {
    const { discussion, identity } = this.attrs;
    const classes = ['CitRegPhoneTopic'];
    if (this.open) classes.push('is-open');
    if (this.truncated) classes.push('is-truncated');
    return (
      <div className={classes.join(' ')}>
        <button type="button" className="CitRegPhoneTopic-toggle" aria-expanded={this.open ? 'true' : 'false'} title={discussion.title()}
          onclick={(e) => { e.stopPropagation(); this.open = !this.open; }}>
          <h1 className="CitRegPhoneTopic-title">{discussion.title()}</h1>
          <i className="fas fa-caret-down CitRegPhoneTopic-caret" aria-hidden="true" />
        </button>
        {this.open ? (
          <div className="CitRegPhoneTopic-bubble">
            <div className="CitRegPhoneTopic-boards">
              <Link href={app.route.tag(identity.primary)}>{identity.primary.name()}</Link>
              {identity.secondaryTags.map((tag) => [<span aria-hidden="true"> · </span>, <Link href={app.route.tag(tag)}>{tag.name()}</Link>])}
            </div>
            <div className="CitRegPhoneTopic-full">{discussion.title()}</div>
            {/* A car's thread (SP-54 (Car Comments)): on phones its "View this car" link lives here, not in the nav row
                (operator, 2026-10-31). */}
            {discussion.attribute('citregCarId') ? (
              <a className="Button Button--block citreg-car-link CitRegPhoneTopic-car" href={`/gallery/car.php?id=${discussion.attribute('citregCarId')}`}>
                {app.translator.trans('citreg-taxonomy.forum.car_thread.view_car')}
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
}

// SP-55 (Forum UX Streamline): the Registry's Year of Manufacture chart (the Home and Gallery "Nose" widget) as a
// graphic-only icon: one bar per year 1955-1975, coloured by Nose as on /statistics/. A fixed snapshot of the
// chart's shape (local registry, 2026-10-31), not live data: it is a link icon, not a report.
const YEAR_CHART_ICON = [
  [0, 1, 4, 2, 0, 4, 3, 1], // 1955-1962, First Nose
  [1, 1, 0, 9, 8], // 1963-1967, Second Nose
  [3, 5, 7, 14, 13, 11, 8, 4], // 1968-1975, Third Nose
];
const NOSE_COLORS = ['#2a78d6', '#eb6834', '#1baf7a'];

function yearChartIcon() {
  const bars = [];
  let x = 0;
  YEAR_CHART_ICON.forEach((counts, nose) => counts.forEach((count) => {
    const h = count ? Math.max(2, Math.round((count / 14) * 26)) : 0;
    if (h) bars.push(<rect x={x} y={28 - h} width="1.6" height={h} fill={NOSE_COLORS[nose]} />);
    x += 2;
  }));
  return (
    <svg viewBox="0 0 42 28" aria-hidden="true" focusable="false">
      {bars}
      <rect x="0" y="27.5" width="42" height="0.5" fill="#9aa5b1" />
    </svg>
  );
}

function installVisualTuner() {
  if (!/^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)) return;
  if (!/[?&]tune=1(&|$)/.test(window.location.search)) return;
  window.setTimeout(() => {
    const script = document.createElement('script');
    script.src = '/_tuner/tuner.js?' + Date.now();
    document.head.appendChild(script);
  }, 1000);
}

// SP-55 (Forum UX Streamline): no discussion-list pane on discussion pages (operator, 2026-10-31: "disable the
// slide out"). Core slides the list in when the mouse nears the left edge and, from 1100px, offers a pin that shifts
// the page 400px. The list is left out of the page and the pane never switches on, so neither the hover nor the
// pin (nor a pin stored in the browser earlier) can appear.
function disableDiscussionPane() {
  extend(DiscussionPage.prototype, 'viewItems', function (items) {
    if (items.has('pane')) items.remove('pane');
  });
  override(Pane.prototype, 'enable', function () {});
}

app.initializers.add('citreg-taxonomy', () => {
  disableDiscussionPane();
  installAnalytics();

  installVisualTuner();

  installHomeReveal();

  installAuthModalBranding();

  installPhoneUpload();
  installUploadPreviewNode();

  // --- CR visual-design scaffold: a real semantic right rail. ---
  // The element is a sibling of #content, so Flarum's routed page tree stays
  // untouched and naturally shrinks via CSS grid. It persists across SPA
  // navigation because .App-content itself is part of the stable shell.
  const appContent = document.querySelector('.App-content');

  // Replace the theme's former static pseudo-element with real, accessible
  // text derived from the active primary category. Run once after boot and
  // again whenever Flarum swaps routed content during SPA navigation.
  window.setTimeout(renderHeaderContextLabel, 0);
  window.setTimeout(syncStickyTopicVisibility, 0);
  window.setTimeout(installDraggableComposers, 0);
  window.setTimeout(repositionEmailConfirmationNotice, 0);
  // STANDING REQUIREMENT, user decision 2026-09-04: must stay enabled
  // regardless of unrelated layout/hero/theme work, until explicitly told
  // otherwise -- see discussionStart.js's own header for why this was
  // pulled into its own file. Do not comment this out to "temporarily" hold
  // back other unrelated work; hold that other work back instead.
  window.setTimeout(startCitRegDiscussion, 0);
  // SP-54 (Car Comments): sign-in from a car page, then back to it; the car thread's link to its car page.
  window.setTimeout(resumeCarReturn, 0);
  installCarThreadLink();

  // A Tab-to-specific-position feature was attempted and removed here
  // (2026-09-02): confirmed via a live RangeError that fof/rich-text's
  // ProseMirror driver indexes cursor positions over its own rendered
  // document, not raw markdown character count, so a position computed
  // from the reference line's markdown source length was invalid and threw
  // before doing anything. Fixing that properly means computing a
  // driver-specific position (real added scope, not a quick patch) --
  // dropped rather than built half-working. Tab now does its ordinary
  // default browser thing on this input.

  new MutationObserver(() => {
    installDraggableComposers();
    repositionEmailConfirmationNotice();
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', () => {
    document.querySelectorAll('.App-composer .Composer').forEach((composer) => {
      if (!composerCanFloat(composer)) {
        dockComposer(composer);
        return;
      }

      if (composer.closest('.App-composer')?.classList.contains('CitRegComposer--floating')) {
        const rect = composer.getBoundingClientRect();
        floatComposerAt(composer, rect.left, rect.top, rect.width);
      }
    });
  });

  if (appContent && !appContent.querySelector('.CitRegForumSidebar')) {
    const sidebar = document.createElement('aside');
    sidebar.className = 'CitRegForumSidebar';
    sidebar.setAttribute('aria-label', 'Forum sidebar');

    appContent.appendChild(sidebar);
    renderSidebar(sidebar);
    new MutationObserver(() => {
      renderSidebar(sidebar);
      renderHeaderContextLabel();
      syncStickyTopicVisibility();
    }).observe(appContent.querySelector('#content'), { childList: true, subtree: true });
  }

  // --- Route registration: one JS route per PHP route, same shapes ---
  // Group routes first, as in extend.php: Mithril takes the first matching route, so registered after the flat
  // ones, /{group} and /{group}/{primary} were matched as /:primary and /:primary/:secondary (To-do #13).
  KNOWN_GROUPS.forEach((group) => {
    app.routes[`citreg.group.${group}`] = { path: `/${group}`, component: IndexPage };
    app.routes[`citreg.tag.${group}`] = { path: `/${group}/:primary`, component: IndexPage };
    app.routes[`citreg.tag.${group}.sub`] = { path: `/${group}/:primary/:secondary`, component: IndexPage };
  });

  app.routes['citreg.tag.flat'] = { path: '/:primary', component: IndexPage };
  app.routes['citreg.tag.flat.sub'] = { path: '/:primary/:secondary', component: IndexPage };

  // --- Internal link generation. NOT actually the single choke point it
  // first looked like -- there are two, both patched here:
  //   1. app.route.tag(tag) -- used by fof/categories' Category.tsx (the
  //      /forum/categories grid) and by our own breadcrumb component.
  //   2. TagLinkButton.initAttrs() -- used by flarum/tags' own addTagList.js,
  //      which populates the sidebar shown on every IndexPage-based page
  //      (i.e. every page this extension renders, since they all reuse
  //      IndexPage). It hardcodes `app.route('tag', {tags: slug})` directly
  //      in its own source, bypassing app.route.tag() entirely -- confirmed
  //      by reading flarum/tags' actual TagLinkButton.js after a real
  //      incognito-window bug report showed the sidebar still linking to
  //      the native /t/{slug} URL even with (1) already fixed and verified.
  // Applies to every non-child tag, primary or secondary -- secondary tags
  // (Technical, Registry, ...) get a pretty flat URL too now, same as
  // primaries; only child tags (none exist yet) still fall back to native,
  // since there's no pretty URL built for those. Both read app.forum
  // lazily (per-call), safe here since both run at click/render time, not
  // at init time.
  const taxonomyRouteForTag = (tag) => {
    const primaryToGroup = app.forum.attribute('citregTaxonomyGroups') || {};
    // Secondary tags never have a group -- only a primary slug can appear
    // as a key in citregTaxonomyGroups (see TaxonomyGroups::all() on the
    // PHP side), so this naturally resolves to the flat route for them.
    const group = primaryToGroup[tag.slug()];
    const routeName = group ? `citreg.tag.${group}` : 'citreg.tag.flat';

    return app.route(routeName, { primary: tag.slug() });
  };

  // Some flarum/tags helpers call app.route('tag', ...) directly instead of
  // app.route.tag(). Wrap the named-route generator itself so those links do
  // not enter the native /t/{slug} SPA route and bypass the server redirect.
  const nativeRoute = app.route;
  const taxonomyAwareRoute = function (name, params, ...rest) {
    if (name === 'tag' && params && params.tags) {
      const tag = findTag(params.tags);
      if (tag && !tag.isChild()) return taxonomyRouteForTag(tag);
    }

    return nativeRoute(name, params, ...rest);
  };
  Object.assign(taxonomyAwareRoute, nativeRoute);
  app.route = taxonomyAwareRoute;

  const nativeRouteTag = app.route.tag;

  app.route.tag = (tag, near) => {
    if (!tag.isChild()) {
      return taxonomyRouteForTag(tag);
    }

    return nativeRouteTag(tag, near);
  };

  const nativeInitAttrs = TagLinkButton.initAttrs;

  TagLinkButton.initAttrs = function (attrs) {
    nativeInitAttrs.call(this, attrs);

    const tag = attrs.model;

    if (tag && !tag.isChild()) {
      attrs.route = taxonomyRouteForTag(tag);
    }
  };

  // 2026-08-30: applying the model-line presentation in initAttrs (above)
  // silently did nothing - confirmed by reading flarum/tags' own
  // TagLinkButton.view() source: it builds `style` fresh from
  // `{ '--color': tag.color() }` on every render and never reads
  // `this.attrs.style` at all, so whatever initAttrs put there was always
  // discarded. Extending view() itself and mutating the *returned* vnode's
  // attrs - after Flarum builds it, before Mithril processes it - is the
  // only point that actually reaches the DOM.
  extend(TagLinkButton.prototype, 'view', function (vnode) {
    const tag = this.attrs.model;

    if (tag && !tag.isChild()) {
      applyModelLineChoicePresentation(vnode, tag, true);
    }
  });

  // Add registered model-line markers to tag choices without changing
  // Flarum's selection behavior.
  extend(TagSelectionModal.prototype, 'content', function (content) {
    markModelLineChoices(content);
  });

  // --- Discussion list filtering: mirrors flarum/tags' own addTagFilter.tsx
  // exactly, reading our route params instead of the native `tags` param. ---
  extend(GlobalSearchState.prototype, 'params', function (params) {
    const primary = m.route.param('primary');
    const secondary = m.route.param('secondary');

    if (primary) {
      params.citregPrimary = primary;
      params.citregSecondary = secondary;
    } else {
      const group = currentGroupSlug();
      if (group) params.citregGroup = group;
    }
  });

  // --- Board-index "partial preview" (DM 2026-09-11, local-source only,
  // not deployed): the discussion's last (or first, when sorted
  // newest/oldest, matching TerminalPost's own showFirstPost() convention
  // below) post's plain text shows physically clipped/faded under the
  // title, reading as "there's more below" rather than a clean "..."
  // truncation. lastPost/firstPost no longer need requesting here - both
  // are now real PHP-side defaults on ListDiscussionsController
  // (extend.php's own Extend\ApiController registration), which also
  // covers the SSR-embedded initial page load a client-only include
  // param can't reach. An image "sliver" companion (per the same
  // conversation) is a deliberate later follow-up: FoF Upload doesn't
  // expose attachment data on the Post API resource today, so that half
  // needs new PHP work this pass doesn't do.
  extend(DiscussionListState.prototype, 'requestParams', function (params) {
    if (this.params.citregPrimary) {
      const filter = params.filter || {};

      // AND semantics (both tags required) needs the free-text `tag:x tag:y`
      // form -- the structured filter.tag key is OR-across-values (confirmed
      // by reading TagFilterGambit's source), which would be wrong here.
      if (this.params.citregSecondary) {
        const gambit = `tag:${this.params.citregPrimary} tag:${this.params.citregSecondary}`;
        filter.q = filter.q ? `${filter.q} ${gambit}` : gambit;
      } else {
        filter.tag = this.params.citregPrimary;
      }

      params.filter = filter;
    } else if (this.params.citregGroup) {
      // Group-index page: every primary tag in this group, OR semantics
      // (correctly the structured filter.tag key here, unlike above).
      const primaryToGroup = app.forum.attribute('citregTaxonomyGroups') || {};
      const primariesInGroup = Object.keys(primaryToGroup).filter((slug) => primaryToGroup[slug] === this.params.citregGroup);

      if (primariesInGroup.length) {
        const filter = params.filter || {};
        filter.tag = primariesInGroup;
        params.filter = filter;
      }
    }
  });

  // The excerpt row itself - inserted between the title (priority 100) and
  // the native info/TerminalPost row (90), so it sits directly under the
  // title as requested. Skipped in search mode: core's own infoItems()
  // already renders a highlighted excerpt there (see DiscussionListItem's
  // own q-branch), a second one would be redundant.
  // Position cutoff, user decision 2026-09-11: only the top 3 rows of the
  // current sorted/filtered view get a preview (positions 0-2); everything
  // else stays heading-only. citregListPosition is set by the DiscussionList
  // extend below - undefined here means something rendered this item
  // outside that path (defensively treated as "don't show", not "always
  // show", so a future core change can't silently make every row preview
  // again).
  const EXCERPT_MAX_POSITION = 3;

  extend(DiscussionList.prototype, 'view', function (vnode) {
    tagDiscussionListItemPositions(vnode, { value: 0 });
  });

  extend(DiscussionListItem.prototype, 'mainItems', function (items) {
    if (this.attrs.params.q) return;
    if ((this.attrs.citregListPosition ?? EXCERPT_MAX_POSITION) >= EXCERPT_MAX_POSITION) return;

    const discussion = this.attrs.discussion;
    const post = this.showFirstPost() ? discussion.firstPost() : discussion.lastPost();

    if (!post || post.contentType() !== 'comment') return;

    const text = (post.contentPlain() || '').trim();
    if (!text) return;

    items.add('citregExcerpt', <div className="CitRegDiscussionExcerpt">{text}</div>, 95);
  });

  // Give the hero access to the DiscussionPage instance. Reply controls use
  // that context to preserve Flarum's native permission and login behavior.
  extend(DiscussionPage.prototype, 'hero', function (hero) {
    if (hero && hero.attrs) hero.attrs.citregPage = this;
  });

  // Local sticky hero-navigation rendition, 2026-09-04. One real component
  // replaces the formerly separate image marker and title items so long titles
  // can truncate without moving the native Follow/Reply controls.
  //
  // Desktop-only: every CitRegDiscussionHero-* rule in forum.less lives inside
  // @media(min-width:992px); below that width the approved native title/tags
  // hero is the design. 2026-10-30: both renditions are now always rendered
  // and forum.less picks one by width. The earlier `innerWidth < 992` gate
  // ran once per render, so a page opened wide and then narrowed (window
  // resize, rotation, DevTools device mode) kept the desktop markup with no
  // styles: the full-width model-line images seen on the live car threads.
  extend(DiscussionHero.prototype, 'items', function (items) {
    // SP-55 (Forum UX Streamline) step 2, operator 2026-10-31 (preview A with the model icon): every board gets
    // the CR header. A model line shows its own drawing; a board without one (Sandbox, General) shows the
    // Citroën Registry chevron in the same cell. A discussion with no primary tag keeps the native band.
    const identity = discussionIdentity(this.attrs.discussion);
    if (!identity) return;
    const { primary, imageLink, secondaryTags } = identity;

    const otherBoards = Object.keys(modelLinePresentation)
      .filter((slug) => !primary || slug !== primary.slug())
      .map((slug) => ({ tag: findTag(slug), presentation: modelLinePresentation[slug] }))
      .filter((entry) => entry.tag);

    // The native 'title' item and flarum/tags' 'tags' label stay in the list
    // for phone/tablet; at desktop width forum.less hides them, since this
    // component carries the title and links the primary tag itself.
    items.add(
      'citregHeroNav',
      <div className="CitRegDiscussionHero-nav">
        <div className="CitRegDiscussionHero-identity">
          {imageLink('CitRegDiscussionHero-sectionImage')}
          <span className="CitRegDiscussionHero-labels">
            <Link href={app.route.tag(primary)}>{primary.name()}</Link>
            <span>Topic:</span>
          </span>
        </div>
        <div className="CitRegDiscussionHero-main">
          <nav className="CitRegDiscussionHero-boards" aria-label="Other forums">
            {otherBoards.map(({ tag, presentation: boardPresentation }) => (
              <Link href={app.route.tag(tag)} title={`${tag.name()} Forum`} aria-label={`${tag.name()} Forum`}>
                <img src={forumAssetUrl(boardPresentation.navigationIcon)} alt="" />
              </Link>
            ))}
          </nav>
          <div className="CitRegDiscussionHero-topicRow">
            <h1 title={this.attrs.discussion.title()}>{this.attrs.discussion.title()}</h1>
            <div className="CitRegDiscussionHero-actions">
              {app.session.user ? <SubscriptionMenu discussion={this.attrs.discussion} /> : null}
              <SplitDropdown
                icon="fas fa-ellipsis-v"
                className="App-primaryControl CitRegDiscussionHero-reply"
                buttonClassName="Button--primary"
                accessibleToggleLabel={app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')}
              >
                {DiscussionControls.controls(this.attrs.discussion, this.attrs.citregPage).toArray()}
              </SplitDropdown>
            </div>
          </div>
        </div>
      </div>,
      20
    );

    // Tablet (768-991px): the same identity, curated to the width: model drawing (or chevron), board and
    // secondary tags, the title, the stats icon. Phones (below 768px, operator 2026-10-31: "[Citroen Registry]
    // [Stats Icon] [Hamb]"): the brand, the stats icon and the menu; the model drawing and the topic move to the
    // nav row below (sidebarItems). forum.less picks the parts by width.
    items.add(
      'citregHeroPhone',
      <div className="CitRegDiscussionHero-phone">
        {/* Operator, 2026-10-31: the brand goes to the site's home page, not the Forum's. */}
        <a href="/" className="CitRegDiscussionHero-phoneBrand" aria-label="Citroën Registry home">
          <img src={app.forum.attribute('logoUrl')} alt="" />
          <span>Citroën Registry</span>
        </a>
        {imageLink('CitRegDiscussionHero-phoneImage')}
        <div className="CitRegDiscussionHero-phoneText">
          <div className="CitRegDiscussionHero-phoneBoards">
            <Link href={app.route.tag(primary)}>{primary.name()}</Link>
            {secondaryTags.map((tag) => [<span aria-hidden="true"> · </span>, <Link href={app.route.tag(tag)}>{tag.name()}</Link>])}
          </div>
          <h1>{this.attrs.discussion.title()}</h1>
        </div>
        <a className="CitRegDiscussionHero-phoneStats" href="/statistics/" title="Registry statistics" aria-label="Registry statistics">
          {yearChartIcon()}
        </a>
        <button type="button" className="Button Button--icon CitRegDiscussionHero-phoneMenu" aria-label="Menu" onclick={(e) => { e.stopPropagation(); app.drawer.show(); }}>
          <i className="fas fa-bars" aria-hidden="true" />
        </button>
      </div>,
      19
    );
  });

  // Preserve topic orientation once the compact discussion hero has scrolled
  // away. The full title remains available on hover/focus and the cell links
  // back to the discussion's canonical first-post route.
  extend(DiscussionPage.prototype, 'sidebarItems', function (items) {
    const discussion = this.discussion;

    if (!discussion) return;

    // Phone nav row (operator, 2026-10-31): "[Model Image] [Post Topic] [Follow Button] [1/1 and arrows] [3dot
    // menu]". The image and topic exist only for that row; forum.less hides them from 768px up.
    const identity = discussionIdentity(discussion);
    if (identity) {
      items.add('citregPhoneImage', identity.imageLink('CitRegPhoneNav-image'), 130);
      items.add('citregPhoneTopic', <PhoneTopic discussion={discussion} identity={identity} />, 125);
    }

    items.add(
      'citregStickyTopic',
      <Link
        className="CitRegStickyTopic"
        href={app.route.discussion(discussion)}
        title={discussion.title()}
        aria-label={`Topic: ${discussion.title()}`}
      >
        <span className="CitRegStickyTopic-label">Topic</span>
        <span className="CitRegStickyTopic-title">{discussion.title()}</span>
      </Link>,
      70
    );
  });

  // --- Visible breadcrumb, inserted above the discussion list on any of
  // our routes. Reads the same route params the filter wiring above does,
  // so what's shown always matches what's actually being filtered. ---
  extend(IndexPage.prototype, 'contentItems', function (items) {
    const primarySlug = m.route.param('primary');
    const secondarySlug = m.route.param('secondary');
    const groupSlug = currentGroupSlug();

    if (!primarySlug && !groupSlug) return;

    const groupLabels = app.forum.attribute('citregTaxonomyGroupLabels') || {};
    const group = groupSlug ? { slug: groupSlug, label: groupLabels[groupSlug] || groupSlug } : null;
    const primary = findTag(primarySlug);
    const secondary = findTag(secondarySlug);
    const primaryPresentation = presentationForTag(primary);
    const primaryIcon = primaryPresentation ? forumAssetUrl(primaryPresentation.navigationIcon) : null;
    const primaryLabel = primaryPresentation ? primaryPresentation.familyName : null;

    items.add(
      'citregTaxonomyBreadcrumb',
      <TaxonomyBreadcrumb group={group} primary={primary} primaryIcon={primaryIcon} primaryLabel={primaryLabel} secondary={secondary} />,
      110
    );
  });

  // --- Browser tab title on our routes (2026-10-29, To-do #12). flarum/tags titles a tag page only when the tag
  // is in a `tags` route param; ours use `primary`/`secondary`, so the tab fell back to "All Discussions" once the
  // app booted. Same text as TaxonomyContent/TaxonomyGroupContent send from the server. Registered after
  // flarum/tags' own setTitle extension, so this one wins. ---
  extend(IndexPage.prototype, 'setTitle', function () {
    const primarySlug = m.route.param('primary');
    const primary = findTag(primarySlug);
    const secondary = findTag(m.route.param('secondary'));
    const groupSlug = currentGroupSlug();

    if (primary) {
      app.setTitle(secondary ? `${primary.name()} – ${secondary.name()}` : primary.name());
    } else if (groupSlug && !primarySlug) {
      const groupLabels = app.forum.attribute('citregTaxonomyGroupLabels') || {};
      app.setTitle(groupLabels[groupSlug] || groupSlug);
    }
  });
});

export default {};
